import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, CheckCircle2, ChevronRight, LayoutDashboard, BellRing, ArrowRight, MapPin } from 'lucide-react';

const MilestoneTracker: React.FC = () => {
    const navigate = useNavigate();

    const handleRunCheck = () => {
        navigate('/eligibility?service=immigration');
    };

    return (
        <div className="bg-white min-h-screen pt-32 pb-20 font-sans">
            {/* Hero Header */}
            <div className="bg-[#011E26] text-white py-20 mb-16 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#009B8F]/20 to-transparent"></div>
                <div className="max-w-7xl mx-auto px-6 relative z-10">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-[#009B8F] rounded-lg">
                            <Calendar className="w-5 h-5 text-[#011E26]" />
                        </div>
                        <span className="text-slate-400 font-bold tracking-widest text-xs uppercase">Journey Management</span>
                    </div>
                    <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
                        Immigration <br /> <span className="text-[#009B8F]">Milestone</span> Tracker
                    </h1>
                    <p className="text-xl text-slate-300 max-w-2xl font-light leading-relaxed">
                        A clear, structured overview of every key step in your visa process. Stop juggling dates
                        and stay ahead of deadlines.
                    </p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6">
                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center mb-32">
                    <div className="space-y-8">
                        <h2 className="text-4xl font-bold text-[#011E26] leading-tight">
                            A Personalised <span className="text-[#009B8F]">Timeline</span> for Your Journey.
                        </h2>
                        <div className="prose prose-lg text-slate-600 space-y-6">
                            <p>
                                From preparation to final decision, every stage is mapped out so you always know what
                                comes next. Once your info is verified, the system generates a timeline based on
                                your visa category and stage.
                            </p>
                            <div className="p-6 bg-slate-50 border-l-4 border-[#009B8F] rounded-r-2xl italic">
                                "Every stage is mapped out so you always know what comes next."
                            </div>
                        </div>
                    </div>
                    <div className="bg-slate-50 p-10 rounded-[3rem] border border-slate-200">
                        <div className="space-y-8 relative">
                            <div className="absolute left-[27px] top-4 bottom-4 w-0.5 bg-slate-200"></div>
                            {[
                                { title: "Profile Evaluation", status: "completed", date: "Jan 15" },
                                { title: "Document Collection", status: "current", date: "In Progress" },
                                { title: "Financial Verification", status: "upcoming", date: "Coming Soon" },
                                { title: "Visa Submission", status: "upcoming", date: "Feb 28 Est." }
                            ].map((item, i) => (
                                <div key={i} className="flex gap-6 items-center relative z-10">
                                    <div className={`w-14 h-14 rounded-full flex items-center justify-center border-4 border-white shadow-md ${item.status === 'completed' ? 'bg-[#009B8F] text-white' :
                                        item.status === 'current' ? 'bg-amber-400 text-white' : 'bg-slate-200 text-slate-400'
                                        }`}>
                                        {item.status === 'completed' ? <CheckCircle2 size={24} /> : <Clock size={24} />}
                                    </div>
                                    <div className="flex-grow bg-white p-5 rounded-2xl shadow-sm border border-slate-100 flex justify-between items-center">
                                        <div>
                                            <h4 className="font-bold text-[#011E26]">{item.title}</h4>
                                            <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">{item.date}</p>
                                        </div>
                                        <ChevronRight className="text-slate-300" size={18} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Feature Highlights */}
                <div className="mb-32">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            {
                                icon: <Clock className="w-10 h-10 text-[#009B8F]" />,
                                title: "Live Updates",
                                desc: "Your timeline updates automatically as you complete steps or add new information recorded in the system."
                            },
                            {
                                icon: <BellRing className="w-10 h-10 text-[#009B8F]" />,
                                title: "Automated Alerts",
                                desc: "Proactive reminders for important dates such as document submissions, appointments, and expiry deadlines."
                            },
                            {
                                icon: <LayoutDashboard className="w-10 h-10 text-[#009B8F]" />,
                                title: "Centralized Dashboard",
                                desc: "All your key information — dates, tasks, progress, and status — displayed in one clean, easy-to-read interface."
                            }
                        ].map((feature, i) => (
                            <div key={i} className="bg-slate-50 p-10 rounded-3xl border border-slate-100 hover:bg-white hover:shadow-xl transition-all group">
                                <div className="mb-6 p-4 bg-white rounded-2xl w-fit group-hover:bg-[#009B8F]/10 transition-colors shadow-sm">
                                    {feature.icon}
                                </div>
                                <h3 className="text-2xl font-bold text-[#011E26] mb-4">{feature.title}</h3>
                                <p className="text-slate-500 leading-relaxed text-sm">{feature.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* CTA Section */}
                <div className="bg-[#011E26] rounded-[4rem] p-12 md:p-24 text-center shadow-2xl relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-96 h-96 bg-[#009B8F]/10 rounded-full blur-[100px] -ml-48 -mt-48"></div>
                    <div className="relative z-10">
                        <MapPin className="text-[#009B8F] mx-auto mb-8 w-12 h-12 opacity-50" />
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">Take Control of Your Timeline</h2>
                        <p className="text-white/70 text-xl mb-12 max-w-2xl mx-auto">
                            Ready to organize your immigration journey? Run our AI check to start building your
                            personalized milestone map.
                        </p>
                        <button
                            onClick={handleRunCheck}
                            className="group bg-[#009B8F] text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-white hover:text-[#011E26] transition-all flex items-center gap-3 mx-auto shadow-xl"
                        >
                            Run AI Check
                            <ArrowRight className="group-hover:translate-x-2 transition-transform" />
                        </button>
                    </div>
                </div>

                <p className="text-center text-slate-400 text-xs mt-12 max-w-2xl mx-auto italic">
                    Disclaimer: This feature provides system-generated timelines based on your inputs and
                    does not offer legal advice. For personalised guidance, users may choose to consult
                    a regulated immigration professional.
                </p>
            </div>
        </div>
    );
};

export default MilestoneTracker;
